import { useMemo } from "react";
import { Box, Paper, Typography, Grid, Card, CardContent, CardActionArea, Button, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import {
  People as PeopleIcon,
  CalendarMonth as TermIcon,
  Class as LectureIcon,
  LocalHospital as StageIcon,
  Event as SeminarIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";

const today = new Date().toISOString().slice(0, 10);

const links = [
  { path: "/academic/profiles", labelKey: "academic.profiles", icon: PeopleIcon },
  { path: "/academic/curriculum", labelKey: "academic.curriculum", icon: TermIcon },
  { path: "/academic/topic-studied", labelKey: "academic.topicStudied", icon: LectureIcon },
  { path: "/academic/stage", labelKey: "academic.year7Stage", icon: StageIcon },
  { path: "/academic/seminars", labelKey: "academic.seminars", icon: SeminarIcon },
  { path: "/academic/assignments", labelKey: "academic.assignments", icon: AssignmentIcon },
];

export default function AcademicIndex() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";

  const { data: seminarsData } = useFetchObjects(
    ["seminars-upcoming"],
    "seminars/?page_size=20&ordering=seminar_date",
    token
  );
  const seminars = useMemo(() => {
    const raw = seminarsData?.results ?? seminarsData;
    const list = Array.isArray(raw) ? raw : [];
    return list.filter((s) => s.seminar_date >= today).slice(0, 5);
  }, [seminarsData]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>{t("academic.title", "Academic Module")}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{t("academic.subtitle", "Manage terms, subjects, enrollments, lectures, Year 7 (Stage), seminars, and assignments.")}</Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: "primary.50", border: "1px solid", borderColor: "primary.light" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>{t("academic.quickActions", "Quick actions")}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<LectureIcon />}
            onClick={() => navigate("/academic/topic-studied")}
          >
            {t("academic.topicStudied", "Topic studied")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<SeminarIcon />}
            onClick={() => navigate("/academic/take-attendance")}
          >
            {t("academic.takeAttendance", "Take seminar attendance")}
          </Button>
          {seminars.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">{t("academic.forSeminar", "For seminar:")}</Typography>
              {seminars.map((s) => (
                <Chip
                  key={s.id}
                  label={`${s.title} (${s.seminar_date})`}
                  onClick={() => navigate("/academic/take-attendance", { state: { seminarId: s.id } })}
                  sx={{ cursor: "pointer" }}
                />
              ))}
            </>
          )}
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>{t("academic.sections", "Sections")}</Typography>
      <Grid container spacing={2}>
        {links.map(({ path, labelKey, icon: Icon }) => (
          <Grid item xs={12} sm={6} md={4} key={path}>
            <Card>
              <CardActionArea onClick={() => navigate(path)} sx={{ p: 2 }}>
                <CardContent>
                  <Icon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                  <Typography variant="h6">{t(labelKey)}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
